// src/components/eframes/EFrame141.tsx
import React, { useState, useEffect } from "react";
import ExecutorDashboardTour from "../ui/ExecutorDashboardTour";
import { useNavigate, useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { buildPlanTasks } from "../../utils/planTasks";
import type { PlanTask } from "../../utils/planTasks";
import { useEFrameData } from "./hooks/useEFrameData";
import { unifiedContactService } from "../../services/unifiedContactService";
import {
  assetAggregationService,
  type UnifiedAsset,
} from "../../services/assetAggregationService";
import { isFullyAssigned } from "../../utils/distributionUtils";
import {
  formatPossessiveEstatePlanLabel,
  formatPossessivePlanLabel,
} from "../../utils/planOwnerLabels";

const BankIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ""} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2 2 7v2h20V7l-10-5Z" />
    <path d="M4 11h16v9H4v-9Z" />
    <path d="M10 13h4v5h-4v-5Z" fill="#fff" />
  </svg>
);

const HeartIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ""} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21s-6.716-4.686-9.646-9.071C-.205 7.4 1.967 3 6.146 3A5.407 5.407 0 0 1 12 6.326 5.407 5.407 0 0 1 17.854 3c4.179 0 6.351 4.4 3.793 8.929C18.716 16.314 12 21 12 21Z" />
  </svg>
);

const CheckCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ""} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z" />
    <path
      d="m10 14 5-5-1.414-1.414L10 11.172 8.414 9.586 7 11l3 3Z"
      fill="#fff"
    />
  </svg>
);

const renderLeftIcon = (type: string, className?: string) => {
  switch (type) {
    case "bank":
      return <BankIcon className={className} />;
    case "heart":
      return <HeartIcon className={`${className ?? ""} text-red-600`} />;
    case "check":
      return <CheckCircleIcon className={`${className ?? ""} text-green-700`} />;
    default:
      return null;
  }
};

type TaskDetailMap = Record<string, string[]>;

interface BeneficiaryInfo {
  contactId: string;
  name: string;
  percentage?: number;
}

interface AssetDistribution {
  id: string;
  assetId: string;
  assetName: string;
  assetCategory: string;
  primaryBeneficiaries: BeneficiaryInfo[];
  contingentBeneficiaries: BeneficiaryInfo[];
  instructions?: string;
  notes?: string;
  documentUrl?: string;
  documentName?: string;
}

const mapCategoryToKey = (displayCat: string): string => {
  const lower = (displayCat || "").toLowerCase();

  if (lower.includes("financial")) return "financial";
  if (lower.includes("real estate") || lower.includes("property")) {
    return "real_estate";
  }
  if (lower.includes("personal")) return "personal";
  if (lower.includes("pet")) return "pets";
  if (lower.includes("digital")) return "digital";
  if (lower.includes("business")) return "business";

  return "personal";
};

function hasAnyWrittenInput(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      if (hasAnyWrittenInput(value[i])) return true;
    }
    return false;
  }

  if (typeof value === "object") {
    const o = value as Record<string, unknown>;

    for (const key in o) {
      if (key === "id") continue;
      if (hasAnyWrittenInput(o[key])) return true;
    }

    return false;
  }

  return false;
}

const getExecutorSummaryRoute = (task: PlanTask, estateId?: string) => {
  if (!estateId) return null;

  const base = `/executor-plan/${estateId}`;

  const executorSummaryRoutes: Record<string, string> = {
    about: `${base}/eframe-135`,
    legacy: `${base}/eframe-173`,
    funeral: `${base}/eframe-223`,
    personal: `${base}/eframe-802`,
    pets: `${base}/eframe-248`,
    inventory: `${base}/eframe-213`,
    debts: `${base}/eframe-238`,
    distribution: `${base}/eframe-247`,
    legal_essentials: `${base}/eframe-215`,
  };

  return executorSummaryRoutes[task.id] ?? null;
};

type LegalDocRow = { type?: string };
type ExecutorRow = { firstName?: string; lastName?: string };
type MemoryCollectionRow = { memories?: string; files?: unknown[] };
type PropertyRow = { currentValue?: number; mortgageBalance?: number };
type FinancialAssetRow = { value?: number };
type PersonalItemRow = { category?: string; estimatedValue?: number };
type DebtRow = { amount?: number };
type ExpenseRow = { frequency?: string; amount?: number };

type DistributionRecord = {
  assetId: string;
  assetCategory?: string;
  primaryBeneficiaries?: Array<{ beneficiaryId: string; percentage?: number }>;
  primaryBeneficiaryId?: string;
  primaryPercentage?: number;
  contingentBeneficiaries?: Array<{ beneficiaryId: string; percentage?: number }>;
  contingentBeneficiaryId?: string;
  instructions?: string;
  notes?: string;
};


const EFrame141: React.FC = () => {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId: string }>();
  const { session, isTaskComplete } = useEFrameData(estateId ?? null);

  const planOwnerName = session.name;

  const planOwnerPlanLabel = formatPossessivePlanLabel(planOwnerName);
  const planOwnerEstatePlanLabel = formatPossessiveEstatePlanLabel(planOwnerName);

  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [taskDetails, setTaskDetails] = useState<TaskDetailMap>({});
  const [shouldRunTour] = useState(
    () => localStorage.getItem('eoe_executor_dashboard_tour_completed') !== 'true'
  );

  const memoryCollectionsComplete =
    Boolean(session.taskProgress?.legacy?.collectionsComplete) ||
    Boolean(session.memoryCollectionsComplete);

  const propertiesTaskComplete = Boolean(isTaskComplete("properties"));

  const inventoryCompleted =
    Boolean(session.taskProgress?.inventory?.completed) ||
    Boolean(isTaskComplete("inventory"));

  const funeralCompleted =
    Boolean(session.taskProgress?.funeral?.completed) ||
    Boolean(isTaskComplete("funeral"));

  const legalEssentialsCompleted =
    Boolean(session.taskProgress?.legal_essentials?.completed) ||
    Boolean(isTaskComplete("legal_essentials"));

  const { mainTasks, additionalTasks } = buildPlanTasks({
    isTaskComplete,
    memoryCollectionsComplete,
    inventoryCompleted,
    propertiesTaskComplete,
    funeralCompleted,
    legalEssentialsCompleted,
  });

  const allTasks = React.useMemo(
    () => [...mainTasks, ...additionalTasks],
    [mainTasks, additionalTasks]
  );

  const grouped = React.useMemo(
    () =>
      ({
        Personal: allTasks.filter((t) => t.category === "Personal"),
        Legal: allTasks.filter((t) => t.category === "Legal"),
        Financial: allTasks.filter((t) => t.category === "Financial"),
      } as const),
    [allTasks]
  );

  useEffect(() => {
    const build = async () => {
      // Executor read-only: session data is authoritative; never fall back to
      // browser localStorage backup keys that belong to the logged-in executor.
      const loadWithFallback = <T,>(_key: string, defaultValue: T): T => defaultValue;

      const details: TaskDetailMap = {};

      const executorsFromNewFlow =
        (session.taskProgress?.executors?.answers?.selectedExecutors as ExecutorRow[] | undefined) || [];
      const executorsFromLegacy =
        (session.taskProgress?.legal_essentials?.answers?.executors as ExecutorRow[] | undefined) || [];
      const executors = executorsFromNewFlow.length > 0 ? executorsFromNewFlow : executorsFromLegacy;
      const legalDocs =
        (session.taskProgress?.legal_documents?.answers?.documents as LegalDocRow[] | undefined) || [];

      const legalDocTypes = legalDocs.reduce<Record<string, number>>((acc, doc) => {
        const t = doc.type ?? "other";
        acc[t] = (acc[t] || 0) + 1;
        return acc;
      }, {});

      const legalDocLabels: Record<string, string> = {
        will: "Will",
        trust: "Living Trust",
        healthcare_power_of_attorney: "Healthcare POA",
        healthcare_directive: "Healthcare Directive",
        other: "Other",
      };

      const legalDocSummary = Object.entries(legalDocTypes).map(
        ([type, count]) => `${count} ${legalDocLabels[type] || type}`
      );

      details["legal_essentials"] = [
        executors.length > 0
          ? `Executors: ${executors
              .map((e) => `${e.firstName ?? ""} ${e.lastName ?? ""}`)
              .join(", ")}`
          : "No executors selected",
        legalDocs.length > 0
          ? `Documents: ${legalDocSummary.join(", ")}`
          : "No legal documents uploaded",
      ];

      const aboutData = session.taskProgress?.about?.answers || {};
      let contactsCount = 0;

      try {
        const contacts = estateId
          ? await unifiedContactService.getContactsForEstate(estateId)
          : await unifiedContactService.getAllContacts();
        contactsCount = contacts.length;
      } catch {
        // ignore contact load errors
      }

      details["about"] = [
        `Name: ${session.name || "Not provided"}`,
        `Email: ${session.email || "Not provided"}`,
        `Phone: ${aboutData.phone || session.phone || "Not provided"}`,
        `Contacts imported: ${contactsCount}`,
      ];

      const memoryCollections =
        (session.taskProgress?.memoryCollections?.answers?.memoryCollections as MemoryCollectionRow[] | undefined) || [];

      const peopleWithMemories = memoryCollections.filter(
        (c) =>
          (c.memories && c.memories.trim().length > 0) ||
          (c.files && Array.isArray(c.files) && c.files.length > 0)
      );

      const totalFiles = memoryCollections.reduce(
        (sum: number, c: MemoryCollectionRow) => sum + ((Array.isArray(c.files) ? c.files.length : 0) || 0),
        0
      );

      details["legacy"] = [
        `${peopleWithMemories.length} people with memories`,
        `${totalFiles} files uploaded`,
        peopleWithMemories.length > 0
          ? "Memory collections created"
          : "No memories added yet",
      ];
      details["memoryCollections"] = details["legacy"];

      const properties =
        (session.taskProgress?.properties?.answers?.properties as PropertyRow[] | undefined) ||
        loadWithFallback<PropertyRow[]>("properties-backup", []);

      const financialAnswers = session.taskProgress?.financial_assets?.answers || {};
      const cashAccounts = (financialAnswers?.cashAccounts as FinancialAssetRow[] | undefined) || loadWithFallback<FinancialAssetRow[]>("cash-accounts-backup", []);
      const investmentAccounts = (financialAnswers?.investmentAccounts as FinancialAssetRow[] | undefined) || loadWithFallback<FinancialAssetRow[]>("investment-accounts-backup", []);
      const retirementAccounts = (financialAnswers?.retirementAccounts as FinancialAssetRow[] | undefined) || loadWithFallback<FinancialAssetRow[]>("retirement-accounts-backup", []);
      const otherFinancialAssets = (financialAnswers?.otherFinancialAssets as FinancialAssetRow[] | undefined) || loadWithFallback<FinancialAssetRow[]>("other-financial-assets-backup", []);
      const allFinancialAssets = [...cashAccounts, ...investmentAccounts, ...retirementAccounts, ...otherFinancialAssets];

      const taxDocs =
        session.taskProgress?.tax_documents?.answers?.documents ||
        loadWithFallback<unknown[]>("tax-documents-backup", []);

      const totalPropertyValue = properties.reduce(
        (sum: number, p: PropertyRow) => sum + (Number(p.currentValue) || 0),
        0
      );

      const totalMortgageBalance = properties.reduce(
        (sum: number, p: PropertyRow) => sum + (Number(p.mortgageBalance) || 0),
        0
      );

      const equity = totalPropertyValue - totalMortgageBalance;

      const totalFinancialValue = allFinancialAssets.reduce(
        (sum: number, a: FinancialAssetRow) => sum + (Number(a.value) || 0),
        0
      );

      const fmt = (n: number) =>
        n.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        });

      details["inventory"] = [
        `${properties.length} ${
          properties.length === 1 ? "property" : "properties"
        } – ${fmt(equity)} equity`,
        `${allFinancialAssets.length} financial assets – ${fmt(
          totalFinancialValue
        )}`,
        `${taxDocs.length} tax documents`,
      ];
      details["properties"] = details["inventory"];

      const funeralPrefs =
        (session.taskProgress?.funeral?.answers?.funeralPreferences as Record<string, unknown> | undefined) ||
        loadWithFallback<Record<string, unknown> | null>("funeral-preferences-backup", null);

      const memorialDetails =
        (session.taskProgress?.funeral?.answers?.memorialDetails as Record<string, unknown> | undefined) ||
        loadWithFallback<Record<string, unknown> | null>("memorial-details-backup", null);

      const funeralFinalInstructions =
        (session.taskProgress?.funeral?.answers?.finalInstructions as Record<string, unknown> | undefined) ||
        loadWithFallback<Record<string, unknown> | null>("final-instructions-backup", null);

      const funeralPreferences = funeralPrefs || {};
      const memDetails = memorialDetails || {};
      const funeralInstructions = funeralFinalInstructions || {};

      const funeralHasMeaningfulData =
        hasAnyWrittenInput(funeralPreferences) ||
        hasAnyWrittenInput(memDetails) ||
        hasAnyWrittenInput(funeralInstructions) ||
        memDetails.reception === true;

      const funeralLines: string[] = [];

      if (!funeralHasMeaningfulData) {
        funeralLines.push("No memorial details planned yet");
      } else {
        const serviceType = funeralPreferences.serviceType;

        if (serviceType && serviceType !== "" && serviceType !== "none") {
          const svc: Record<string, string> = {
            traditional: "Traditional funeral service",
            memorial: "Memorial service",
            celebration: "Celebration of life",
          };

          const svcLabel = svc[serviceType as string] || String(serviceType);
          if (svcLabel.trim()) funeralLines.push(svcLabel.trim());
        }

        let line2 = "";

        if (
          funeralPreferences.customLocation &&
          String(funeralPreferences.customLocation).trim()
        ) {
          line2 = String(funeralPreferences.customLocation).trim();
        } else if (funeralPreferences.location) {
          const loc: Record<string, string> = {
            funeral_home: "Funeral home",
            religious: "Religious institution",
            home: "Family home",
            outdoor: "Outdoor location",
            other: "Other location",
          };

          line2 = loc[funeralPreferences.location as string] || "";
        }

        if (!line2 && memDetails.reception === true) {
          line2 = "Reception planned";
        }

        if (
          !line2 &&
          memDetails.receptionDetails &&
          String(memDetails.receptionDetails).trim()
        ) {
          line2 = "Reception planned";
        }

        if (line2) funeralLines.push(line2);

        funeralLines.push("Memorial details planned");
      }

      details["funeral"] = funeralLines.slice(0, 3);

      const personalItems =
        (session.taskProgress?.personal?.answers?.personalItems as PersonalItemRow[] | undefined) ||
        loadWithFallback<PersonalItemRow[]>("personal-items-backup", []);

      const totalItemValue = personalItems.reduce(
        (sum: number, i: PersonalItemRow) => sum + (Number(i.estimatedValue) || 0),
        0
      );

      const categoryBreakdown = personalItems.reduce<Record<string, number>>((acc, item) => {
        const cat = item.category || "custom";
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {});

      const categoryLabels: Record<string, string> = {
        documents: "Documents",
        physical_assets: "Physical Assets",
        collectibles: "Collectibles",
        electronics: "Electronics",
        digital_assets: "Digital Assets",
        custom: "Other",
      };

      details["personal"] = [
        `${personalItems.length} items – ${fmt(totalItemValue)}`,
        ...Object.entries(categoryBreakdown).map(
          ([cat, count]) => `${count} ${categoryLabels[cat] || cat}`
        ),
      ];

      const debts = (session.taskProgress?.debts?.answers?.debts as DebtRow[] | undefined) || [];
      const expenses =
        (session.taskProgress?.monthly_expenses?.answers?.expenses as ExpenseRow[] | undefined) || [];

      const totalDebt = debts.reduce(
        (sum: number, d: DebtRow) => sum + (Number(d.amount) || 0),
        0
      );

      const calcMonthly = (exp: ExpenseRow) => {
        const amt = Number(exp.amount) || 0;
        switch (exp.frequency) {
          case "quarterly":
            return amt / 3;
          case "annual":
            return amt / 12;
          default:
            return amt;
        }
      };

      const totalMonthlyExpenses = expenses.reduce(
        (sum: number, e: ExpenseRow) => sum + calcMonthly(e),
        0
      );

      details["debts"] = [
        `${debts.length} outstanding debts – ${fmt(totalDebt)}`,
        `${expenses.length} monthly expenses – ${fmt(totalMonthlyExpenses)}/mo`,
      ];

      const rawDistributions =
        session.taskProgress?.assetDistribution?.answers?.distributions || {};

      const assets = assetAggregationService.getAllAssets(session, { useLocalBackups: false });

      let contacts: { id: string; firstName: string; lastName: string }[] = [];

      try {
        contacts = estateId
          ? await unifiedContactService.getContactsForEstate(estateId)
          : await unifiedContactService.getAllContacts();
      } catch (e) {
        console.error("[EFrame141] Failed to load contacts for summary", e);
      }

      const distArray: DistributionRecord[] = Array.isArray(rawDistributions)
        ? (rawDistributions as DistributionRecord[])
        : Object.values(rawDistributions as Record<string, DistributionRecord>);

      const manuallyAssignedIds = new Set<string>();

      const hydrated = distArray
        .map((d) => {
          const asset = assets.find((a: UnifiedAsset) => a.id === d.assetId);

          if (!asset) return null;

          manuallyAssignedIds.add(d.assetId);

          const assetName = asset.name || "Unknown Asset";
          const assetCat =
            d.assetCategory || asset.category || asset.sourceType || "personal";

          let primaryBeneficiaries: BeneficiaryInfo[] = [];

          if (
            Array.isArray(d.primaryBeneficiaries) &&
            d.primaryBeneficiaries.length > 0
          ) {
            primaryBeneficiaries = d.primaryBeneficiaries.map((pb) => {
              const contact = contacts.find((c) => c.id === pb.beneficiaryId);

              return {
                contactId: pb.beneficiaryId,
                name: contact
                  ? `${contact.firstName} ${contact.lastName}`
                  : "Unknown Beneficiary",
                percentage:
                  typeof pb.percentage === "number" ? pb.percentage : 100,
              };
            });
          } else if (d.primaryBeneficiaryId) {
            const contact = contacts.find(
              (c) => c.id === d.primaryBeneficiaryId
            );

            primaryBeneficiaries = [
              {
                contactId: d.primaryBeneficiaryId,
                name: contact
                  ? `${contact.firstName} ${contact.lastName}`
                  : "Unknown Beneficiary",
                percentage:
                  typeof d.primaryPercentage === "number"
                    ? d.primaryPercentage
                    : 100,
              },
            ];
          }

          let contingentBeneficiaries: BeneficiaryInfo[] = [];

          if (
            Array.isArray(d.contingentBeneficiaries) &&
            d.contingentBeneficiaries.length > 0
          ) {
            contingentBeneficiaries = d.contingentBeneficiaries.map((cb) => {
              const contact = contacts.find(
                (c) => c.id === cb.beneficiaryId
              );

              return {
                contactId: cb.beneficiaryId,
                name: contact
                  ? `${contact.firstName} ${contact.lastName}`
                  : "Unknown Beneficiary",
                percentage:
                  typeof cb.percentage === "number" ? cb.percentage : 100,
              };
            });
          } else if (d.contingentBeneficiaryId) {
            const contact = contacts.find(
              (c) => c.id === d.contingentBeneficiaryId
            );

            contingentBeneficiaries = [
              {
                contactId: d.contingentBeneficiaryId,
                name: contact
                  ? `${contact.firstName} ${contact.lastName}`
                  : "Unknown Beneficiary",
                percentage: 100,
              },
            ];
          }

          const item: AssetDistribution = {
            id: d.assetId || "unknown-id",
            assetId: d.assetId || "unknown-id",
            assetName,
            assetCategory: mapCategoryToKey(assetCat),
            primaryBeneficiaries,
            contingentBeneficiaries,
            instructions: d.instructions,
            notes: d.notes,
          };

          return item;
        })
        .filter((item): item is AssetDistribution => item !== null);

      const preAssigned: AssetDistribution[] = assets
        .filter(
          (asset) =>
            !manuallyAssignedIds.has(asset.id) &&
            asset.existingBeneficiaries &&
            asset.existingBeneficiaries.length > 0
        )
        .map((asset) => ({
          id: asset.id,
          assetId: asset.id,
          assetName: asset.name || "Unknown Asset",
          assetCategory: mapCategoryToKey(
            asset.category || asset.sourceType || "personal"
          ),
          primaryBeneficiaries: asset.existingBeneficiaries!.map((eb) => ({
            contactId: eb.contactId || "",
            name: eb.name || "Unknown Beneficiary",
            percentage: eb.percentage || 100,
          })),
          contingentBeneficiaries: [],
          instructions: undefined,
          notes: undefined,
        }));

      const distributions: AssetDistribution[] = [...hydrated, ...preAssigned];

      const flaggedAssets: {
        id: string;
        name: string;
        category: string;
        reason: string;
      }[] = [];

      const allAssets = assetAggregationService.getAllAssets(session, { useLocalBackups: false });

      for (const asset of allAssets) {
        const dist = distributions.find((d) => d.assetId === asset.id);

        if (
          !dist ||
          !dist.primaryBeneficiaries ||
          dist.primaryBeneficiaries.length === 0
        ) {
          flaggedAssets.push({
            id: asset.id,
            name: asset.name || "Unknown Asset",
            category: asset.sourceType || "personal",
            reason: "No beneficiaries assigned",
          });
        } else {
          const total = dist.primaryBeneficiaries.reduce(
            (sum, b) => sum + (Number(b.percentage) || 0),
            0
          );

          if (total !== 100) {
            flaggedAssets.push({
              id: asset.id,
              name: asset.name || "Unknown Asset",
              category: asset.sourceType || "personal",
              reason: `${total}% assigned — 100% required`,
            });
          }
        }
      }

      const assignedDistributions = distributions.filter((d) =>
        isFullyAssigned(d)
      );

      const beneficiaryIdSeen: Record<string, true> = {};

      for (const dist of assignedDistributions) {
        for (const pb of dist.primaryBeneficiaries) {
          if (pb.contactId) beneficiaryIdSeen[pb.contactId] = true;
        }
      }

      const beneficiaryCount = Object.keys(beneficiaryIdSeen).length;
      const totalAssetsCount = allAssets.length;

      let distributionStatusLine: string;

      if (totalAssetsCount === 0) {
        distributionStatusLine = "No distribution plan yet";
      } else if (flaggedAssets.length === 0) {
        distributionStatusLine = "Distribution plan created";
      } else {
        distributionStatusLine = "Some assets unassigned";
      }

      details["distribution"] = [
        `${totalAssetsCount} ${
          totalAssetsCount === 1 ? "asset" : "assets"
        }`,
        `${beneficiaryCount} ${
          beneficiaryCount === 1 ? "beneficiary" : "beneficiaries"
        }`,
        distributionStatusLine,
      ];

      const petsData =
        session.taskProgress?.pets?.answers?.petsAndDependants ||
        loadWithFallback("pets-dependants-backup", null);

      const petsCount = petsData?.pets?.length || 0;
      const dependentsCount = petsData?.dependants?.length || 0;

      const financialArrangements =
        session.taskProgress?.pets?.answers?.financialArrangements;

      const finalInstructions =
        session.taskProgress?.pets?.answers?.finalInstructions;

      const petsList = petsData?.pets || [];
      const dependantsList = petsData?.dependants || [];

      let carePlanDocumented = false;

      for (let i = 0; i < petsList.length && !carePlanDocumented; i++) {
        if (hasAnyWrittenInput(petsList[i])) carePlanDocumented = true;
      }

      for (let i = 0; i < dependantsList.length && !carePlanDocumented; i++) {
        if (hasAnyWrittenInput(dependantsList[i])) carePlanDocumented = true;
      }

      if (!carePlanDocumented && hasAnyWrittenInput(financialArrangements)) {
        carePlanDocumented = true;
      }

      if (!carePlanDocumented && hasAnyWrittenInput(finalInstructions)) {
        carePlanDocumented = true;
      }

      details["pets"] = [
        `${petsCount} ${petsCount === 1 ? "pet" : "pets"}`,
        `${dependentsCount} ${
          dependentsCount === 1 ? "dependent" : "dependents"
        }`,
        carePlanDocumented ? "Care plan documented" : "No care plan yet",
      ];

      setTaskDetails(details);
    };

    build();
  }, [session, isTaskComplete]);

  const toggleTask = (id: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const TaskRow = ({ task, dataTourId }: { task: PlanTask; dataTourId?: string }) => {
    const isExpanded = expandedTasks.has(task.id);
    const details = taskDetails[task.id] ?? [];
    const executorSummaryRoute = getExecutorSummaryRoute(task, estateId);

    const navigateToSummary = () => {
      if (!executorSummaryRoute) return;
      navigate(executorSummaryRoute);
    };

    return (
      <div className="w-full min-h-[85px] max-w-[830px] rounded-lg border border-stone-200 bg-white overflow-hidden shadow-sm">
        <div className="w-full px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 text-left">
          <div className="flex-shrink-0">
            {renderLeftIcon(task.icon, "w-5 h-5 text-cyan-700")}
          </div>

          <div className="min-w-0 flex-1">
            <span className="font-sans text-base sm:text-lg font-medium text-stone-800 block whitespace-normal break-words leading-snug">
              {task.title}
            </span>
          </div>

          <div data-tour={dataTourId} className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto sm:ml-auto">
            {executorSummaryRoute && (
              <button
                type="button"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigateToSummary();
                }}
                className="inline-flex w-full sm:w-auto items-center justify-center rounded-sm bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800"
              >
                View
              </button>
            )}

            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleTask(task.id);
              }}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Collapse details" : "Expand details"}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-stone-300 bg-white text-stone-500 hover:border-stone-500 hover:text-stone-800"
            >
              <svg
                className={`w-5 h-5 ${isExpanded ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="px-6 pb-5 pt-3 border-t border-stone-100 bg-stone-50">
            {details.length > 0 ? (
              <ul className="space-y-2">
                {details.map((line, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-stone-700 flex items-start gap-2"
                  >
                    <span className="text-cyan-600 mt-0.5">•</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-stone-500">
                No summary details available yet.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const Section = ({
    title,
    tasks,
    dataTourId,
  }: {
    title: string;
    tasks: PlanTask[];
    dataTourId?: string;
  }) => (
    <section className="mt-8">
      <h2 className="font-sans text-lg font-semibold text-stone-800 mb-3">
        {title}
      </h2>

      {tasks.length === 0 ? (
        <p className="text-sm text-stone-500 max-w-[830px]">
          No tasks in this section.
        </p>
      ) : (
        <div className="space-y-3">
          {tasks.map((t, i) => (
            <TaskRow key={t.id} task={t} dataTourId={i === 0 ? dataTourId : undefined} />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <>
      <ExecutorDashboardTour shouldRun={shouldRunTour} />
      <ResponsiveContainer>
        <ResponsiveContent>
          <div data-tour="executor-dashboard-content" className="w-full max-w-[835px]">
            <ResponsiveHeader title={planOwnerPlanLabel} />

            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              {`${planOwnerEstatePlanLabel} at a glance. View all directives and records in one place.`}
            </p>

            <div data-tour="executor-plan-summary-tiles">
              <Section title="Personal Wishes" tasks={grouped.Personal} dataTourId="executor-expandable-section" />
              <Section title="Financial Guidance" tasks={grouped.Financial} />
              <Section title="Legal Directives" tasks={grouped.Legal} />
            </div>
          </div>
        </ResponsiveContent>
      </ResponsiveContainer>
    </>
  );
};

export default EFrame141;